import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { StepHeading, StepShell } from "@codel/dova-ds";

import { ApiError } from "@/api/client";
import { PublicApiError } from "@/api/public-fetch";
import {
  SEND_CODE_LOCKED_OUT_STATUS,
  SEND_CODE_THROTTLED_STATUS,
} from "@/esign/sendCodeStatus";
import { useMeContext } from "../../MeApp";
import {
  VerifyCodeFields,
  VERIFY_CODE_SUBMIT_LABEL,
  VERIFY_CODE_SUBMITTING_LABEL,
} from "../../../shared/VerifyCodeFields";
import {
  reportVerificationSkipped,
  requestConsentEnvelope,
  sendEsignCode,
  verifyEsignCode,
  type VerificationSkipCause,
} from "../../consentEnvelope";
import { readEsignLinkToken, writeEsignLinkToken, writeEsignSessionToken } from "../../esignLink";
import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";
import type { OnboardingNavState } from "../useOnboardingForm";

/**
 * S3b — verify the client's email, several steps before they sign.
 *
 * Until now a client answered every onboarding question with NO identity check at all and
 * met the one-time code at the very end, bundled with the document and the signature.
 * This step moves the code to where the email was just entered, so identity is
 * established while the client is still thinking about their contact details, and the end
 * of the flow is left with the thing that actually needs deliberation: reading and signing
 * the consent.
 *
 * NATIVE, NOT A ROUND TRIP. The code is requested and submitted from here against the
 * existing signer endpoints. Bouncing to the signer app and back would break context twice
 * inside a flow whose whole purpose is to stop doing that.
 *
 * ── THIS STEP CAN NEVER BLOCK THE FLOW ───────────────────────────────────────────────
 * It is an OPTIMISATION of the common case, not a gate. That has to hold at BOTH calls it
 * makes, and the second one is the easier to get wrong: an earlier version degraded
 * gracefully when the ENVELOPE could not be created, but left a generic "we couldn't
 * verify that code" on screen when `/verify` itself failed — no continue control, no way
 * forward, the optimisation turned into exactly the mid-onboarding gate it was designed
 * never to be. The `/verify` failures are the ones that matter most, because that is where
 * the IDV-6 database guard rejects a second partner sharing an email address.
 *
 * So: a wrong PIN is the ONE failure the client can fix, and only that one keeps them
 * here. Everything else — at either call — degrades to "we'll do this at the end" and
 * advances, because the end of the flow can still do all of this and always could.
 *
 * The reachable causes, none of which is a reason to strand somebody:
 *   - the email on the identity step is OPTIONAL and skippable → 409 "An email address is
 *     required before signing";
 *   - a legacy couple has no ClientGroup → 409 "Consent signing requires a client group";
 *   - two partners sharing one email trip the IDV-6 guard at VERIFICATION time, which
 *     surfaces as a 500 from `/verify` (pre-existing, on the signer rail, and not this
 *     step's to fix — but this step is now where a client would meet it).
 *
 * Every degradation is reported to the server, not just to the console. A skip that leaves
 * no durable trace is indistinguishable from the optimisation never running, so a
 * fleet-wide regression would look exactly like a healthy flow.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * WHOSE TOKEN IS THIS? The step asks the server that question rather than answering it
 * itself. A carried token that resolves and reports `email_verified` can still belong to
 * another practice, or be bound to an address the client has since changed — both look
 * identical from here. `carried_token_status` is the server's answer for THIS run, and
 * it describes the carried envelope specifically, not the signer's newest one.
 */

// No "skipped" phase. Every reason this step cannot run ends in advancing, not in a
// screen — see `skip` below.
type Phase = "preparing" | "code";

/** Where the signer app should return a client who reaches it from the SIGNING step. */
const SIGNING_RETURN_TO = "/me/onboarding/handoff";

const VERIFIED_STATUSES = new Set(["email_verified", "econ_accepted"]);
const FINISHED_STATUSES = new Set(["signed", "sealed"]);

function statusCodeOf(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status;
  if (error instanceof PublicApiError) return error.status;
  return undefined;
}

export function S3bVerifyEmail({
  onNext,
  onBack,
  currentStepIndex,
  totalSteps,
}: StepProps) {
  const location = useLocation();
  // Same mechanism as S8Consent, same reason: `prepare()` below is a passive-arrival
  // effect that decides "nothing to do here" and advances unconditionally — the exact
  // shape `goBack()` re-triggers on every backward re-mount. `skip`'s own docstring
  // already names the symptom: `no_link_token` with `pending_status: email_verified`
  // means "already verified, reachable by pressing Back from the next step" — pressing
  // Back into THAT state re-ran this effect, decided "already done," and advanced
  // FORWARD again, bouncing the client right back to where they pressed Back from.
  const arrivedViaBack =
    (location.state as OnboardingNavState | null)?.navDirection === "back";
  const { couple, partner } = useMeContext();
  const partnerId = partner?.id ?? "";
  const coupleId = couple?.id ?? "";

  const [phase, setPhase] = useState<Phase>("preparing");
  const [token, setToken] = useState("");
  const [deliveryEmail, setDeliveryEmail] = useState<string | null>(null);
  const [code, setCode] = useState("");
  // The plaintext code, when the API is running locally and volunteered it. Drives
  // the [DEV] button below; always "" against staging or production.
  const [devCode, setDevCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  // Setup runs once per mount. Without this guard React 18's development double-invoke
  // would request two envelopes and email two codes, and the client would be holding
  // whichever one arrived second with no way to know that.
  const setupStarted = useRef(false);

  // Cleared on every (re-)mount, set by the effect cleanup. EVERY navigation and state
  // update that happens after an await is guarded on it, because this step awaits two
  // round trips and the client can press Back through both: a late `onNext()` would then
  // navigate a client who has already navigated themselves, undoing their own Back with
  // no way to see why.
  //
  // A ref rather than the effect-local `let` used in `S8Consent`, and the difference is
  // forced by `setupStarted` above. That guard means the setup never re-runs, so an
  // effect-local flag set by a cleanup would stay set for the lifetime of the request
  // already in flight — and React 18's development double-invoke cleans up and remounts
  // immediately, which would strand the step on "Sending your code…" every time. Clearing
  // it at the top of the effect re-arms the live request on a remount and on any
  // dependency change, and only a real unmount leaves it set.
  const abandoned = useRef(false);

  // Continues in whichever direction this arrival came from, rather than always forward.
  // This step never has content to show once it decides there is nothing to do here (see
  // `skip`'s own docstring below) — forward, that is `onNext()`, same as always; backward,
  // advancing forward instead is exactly what turned "press Back" into a silent bounce.
  const advance = useCallback(() => {
    if (arrivedViaBack) {
      onBack();
    } else {
      onNext();
    }
  }, [arrivedViaBack, onBack, onNext]);

  const skip = useCallback(
    (cause: VerificationSkipCause, detail?: unknown, statusCode?: number) => {
      // Console for the developer in front of it; server for everyone who is not.
      console.warn(`[me] early email verification skipped: ${cause}`, detail ?? "");
      reportVerificationSkipped(partnerId, cause, statusCode);

      // ADVANCE. It used to render "We'll verify your email later", and that screen was
      // wrong in every case it appeared. This step is an OPTIMISATION: verification is
      // going to happen at signing regardless, and doing it early only saves the client
      // a round trip. When the optimisation cannot run, nothing has been lost and there
      // is nothing for the client to do — so telling them something went wrong invents a
      // problem, one screen before they sign a legal document.
      //
      // It was worst in the case that is not a failure at all: `no_link_token` with a
      // `pending_status` of email_verified means the client is ALREADY VERIFIED, and is
      // reachable by pressing Back from the next step. They were shown an apology for a
      // thing that had succeeded — and, until `advance` above, the reachable-by-Back case
      // is exactly the one that would then bounce them straight forward again.
      //
      // The server still hears about every cause: `reportVerificationSkipped` above is
      // what makes a fleet-wide regression visible, and it runs before this returns.
      //
      // Guarded, per the note on `abandoned`: `skip` is reached from inside the async
      // setup, and a late advance would navigate a client who has already pressed Back.
      if (abandoned.current) return;
      // `verification_failed` is NEVER reached from the passive setup effect below — only
      // from an explicit user action (`submitCode` submitting a wrong-but-unfixable code,
      // or `resend`'s locked-out case), each already past its own "should this be shown to
      // the client" decision and choosing to move forward regardless of how this screen was
      // originally reached. `arrivedViaBack` describes the ARRIVAL, not this later action,
      // and stays true for the rest of this mount — using `advance()` here would route a
      // client who explicitly tried to verify (forward intent) backward instead, on a stale
      // signal from how they got here in the first place. `setup_failed`/`no_link_token`
      // are the two causes the passive effect below actually raises, and for THOSE the
      // direction-aware `advance()` is exactly the fix this file exists for.
      if (cause === "verification_failed") {
        onNext();
      } else {
        advance();
      }
    },
    [advance, onNext, partnerId],
  );

  const sendCodeFor = useCallback(
    async (linkToken: string) => {
      setToken(linkToken);
      // NOT GUARDED HERE, AND THAT IS THE POINT. A send the server refused — throttled,
      // or the envelope locked — now comes back as an error status and THROWS, so it
      // never reaches the `setPhase("code")` below. It used to come back 200 with the
      // refusal in the body, so this advanced to the code-entry screen and told the
      // client a code was on its way that had never been sent.
      //
      // The throw is caught by the caller in the setup effect, which reports the skip
      // (with the status code, so a fleet-wide throttle is visible in telemetry) and
      // moves the client ON, past a step that is an optimisation and must never gate
      // onboarding. The refusal surfaces where it can be acted on — the signing screen
      // owns the retry and shows the throttle message there.
      const sent = await sendEsignCode(linkToken);
      if (abandoned.current) return;
      setDeliveryEmail(sent.intended_email ?? null);
      // Dev only, and absent from the payload on any real deployment.
      setDevCode(sent.dev_code ?? "");
      setPhase("code");
    },
    [],
  );

  useEffect(() => {
    abandoned.current = false;
    if (!partnerId || !coupleId || setupStarted.current) return;
    setupStarted.current = true;

    const prepare = async () => {
      // Whatever this tab is holding goes to the server for judgement rather than being
      // trusted here. Sending it is what lets the server tell "the client already verified
      // in THIS run" apart from "the client is holding something from a different one",
      // which are indistinguishable from the browser and end very differently.
      const carried = readEsignLinkToken(coupleId, partnerId);

      let envelope;
      try {
        envelope = await requestConsentEnvelope(partnerId, {
          return_to: SIGNING_RETURN_TO,
          ...(carried ? { carried_token: carried } : {}),
        });
      } catch (error) {
        if (abandoned.current) return;
        skip("setup_failed", error, statusCodeOf(error));
        return;
      }
      if (abandoned.current) return;

      if (envelope.signed) {
        advance();
        return;
      }

      // The server confirmed the carried token belongs to this run, and told us the state
      // of that envelope specifically.
      if (carried && envelope.carried_token_status) {
        if (
          VERIFIED_STATUSES.has(envelope.carried_token_status) ||
          FINISHED_STATUSES.has(envelope.carried_token_status)
        ) {
          advance();
          return;
        }
        try {
          await sendCodeFor(carried);
        } catch (error) {
          if (abandoned.current) return;
          skip("setup_failed", error, statusCodeOf(error));
        }
        return;
      }

      if (!envelope.link_token) {
        // A verified envelope exists for this run but this browser cannot address it.
        // The signing step owns that reconciliation, with the server's view in hand.
        skip("no_link_token", { pending_status: envelope.pending_status });
        return;
      }

      writeEsignLinkToken(coupleId, partnerId, envelope.link_token);
      try {
        await sendCodeFor(envelope.link_token);
      } catch (error) {
        if (abandoned.current) return;
        skip("setup_failed", error, statusCodeOf(error));
      }
    };

    void prepare();
    return () => {
      abandoned.current = true;
    };
  }, [advance, coupleId, partnerId, sendCodeFor, skip]);

  /** DEV ONLY: verify with the code the local API volunteered.
   *
   *  It runs the REAL `verifyEsignCode` rather than jumping the step, which is
   *  the whole point of replacing the old skip: a bypass left the envelope
   *  unverified, and the signing step then correctly behaved like an
   *  unverified signer — so the thing being tested was never the thing being
   *  exercised. This ends with a live signer session, exactly as typing the
   *  code by hand would. */
  const verifyWithDevCode = async () => {
    if (!token || devCode.length !== 6) return;
    setCode(devCode);
    setVerifying(true);
    setCodeError("");
    try {
      const response = await verifyEsignCode(token, devCode);
      if (abandoned.current) return;
      if (response.session_token) writeEsignSessionToken(token, response.session_token);
      onNext();
    } catch (error) {
      if (abandoned.current) return;
      setCodeError(`[DEV] ${String(error)}`);
    } finally {
      if (!abandoned.current) setVerifying(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || code.length !== 6) return;
    setVerifying(true);
    setCodeError("");
    try {
      const response = await verifyEsignCode(token, code);
      if (abandoned.current) return;
      // The signer session cookie is now set for `/esign/{token}` too, but it is a
      // cross-site cookie a browser's own third-party-cookie policy can silently never
      // store (SESSCRED-1) — so the token from the body is what actually carries forward
      // to the S8 hand-off. The signing step still asks the API for status, never local
      // state, for whether the envelope itself is verified; this is only the credential
      // that gets it to `/document` without an extra, avoidable code re-entry.
      if (response.session_token) writeEsignSessionToken(token, response.session_token);
      onNext();
    } catch (error) {
      // A WRONG CODE IS THE ONLY THING THE CLIENT CAN FIX, so it is the only thing that
      // keeps them on this screen. Anything else — a lockout, the IDV-6 guard rejecting a
      // shared address, a 5xx, the network — is not theirs to correct, and leaving them
      // on a PIN form they cannot satisfy is the gate this step must never become.
      if (abandoned.current) return;
      if (error instanceof PublicApiError && error.detail === "invalid") {
        setCodeError("That code wasn't right. Check it and try again.");
        return;
      }
      skip("verification_failed", error, statusCodeOf(error));
    } finally {
      if (!abandoned.current) setVerifying(false);
    }
  };

  const resend = async () => {
    if (!token || resending) return;
    setResending(true);
    setCodeError("");
    try {
      // THE RESPONSE IS CONSUMED HERE TOO, and forgetting it is the trap this file
      // has already fallen into once. Issuing a code REPLACES the outstanding one
      // (LINK-2 semantics), so a resend that ignores its answer leaves the [DEV] bar
      // offering the code this very request just invalidated — tapping it then fails
      // verification, and the flow looks broken to whoever is debugging it. Local and
      // test only (`dev_code` is absent from the payload on every real deployment), so
      // no client sees it; it is a false signal at exactly the wrong moment.
      //
      // ON THE SUCCESS PATH ONLY. A refused send throws straight past this, which is
      // correct: a throttled resend does not mint anything, so the code the bar is
      // holding is still the live one and blanking it would be a second wrong answer.
      const sent = await sendEsignCode(token);
      if (abandoned.current) return;
      setDevCode(sent.dev_code ?? "");
      setResent(true);
    } catch (error) {
      if (abandoned.current) return;
      // A REFUSED SEND THROWS, so "Sent" is now unreachable unless a code really went
      // out. It used to arrive as a 200 with the refusal in the body, which this
      // `catch` never saw: the screen confirmed "Sent. It can take a minute to
      // arrive." and the client waited for an email that was never dispatched.
      const status = statusCodeOf(error);

      // THROTTLED — say what actually happened. "That code didn't send" describes a
      // delivery fault and invites the one action that cannot help, which is pressing
      // the button again. They STAY on this screen deliberately: a refused resend does
      // not touch the outstanding code (`issue_otp` returns before minting), so the
      // code already in their inbox still works and this form is still satisfiable.
      if (status === SEND_CODE_THROTTLED_STATUS) {
        setCodeError(
          "You've asked for a code a few too many times. Your last code still works — or try again a little later.",
        );
        return;
      }

      // LOCKED OUT — the one case that must NOT stay here, for the reason spelled out
      // on `submitCode`: the outstanding code is destroyed and nothing the client can
      // type will satisfy this form for the next fifteen minutes. Leaving them on a PIN
      // field they cannot answer is the gate this step must never become, so it drops
      // through to verifying at signing, exactly as every other unfixable failure does.
      if (status === SEND_CODE_LOCKED_OUT_STATUS) {
        skip("verification_failed", error, status);
        return;
      }

      setCodeError("That code didn't send. Try again.");
    } finally {
      if (!abandoned.current) setResending(false);
    }
  };

  if (phase === "preparing") {
    return (
      <StepShell
        showBack
        onBack={onBack}
        currentStep={currentStepIndex}
        totalSteps={totalSteps}
        footer={<Button disabled>Sending your code…</Button>}
      >
        <StepHeading title="Verifying your email">
          One moment. A code is on its way to the address you just gave.
        </StepHeading>
      </StepShell>
    );
  }

  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      onSubmit={submitCode}
      footer={
        <Button type="submit" disabled={code.length !== 6 || verifying}>
          {verifying ? VERIFY_CODE_SUBMITTING_LABEL : VERIFY_CODE_SUBMIT_LABEL}
        </Button>
      }
    >
      {/*
        DEV-only: verify with the code the local API handed back, so the step can
        be completed without tailing a log for a value that exists nowhere else
        (`issue_otp` keeps a keyed hash; the local transport is FakeEmailClient,
        which logs nothing of its own).

        It VERIFIES rather than skips. The earlier version called `onNext()` and
        left the envelope at `created`, which meant the signing step afterwards
        correctly asked for a code and the path being tested was never the path
        being exercised.

        Two gates, and both must hold: `import.meta.env.DEV` so it never ships,
        and a `devCode` that only a local/test API puts in the response.
      */}
      {import.meta.env.DEV && devCode.length === 6 && (
        <button
          type="button"
          onClick={() => void verifyWithDevCode()}
          disabled={verifying}
          className="fixed inset-x-0 top-0 z-50 bg-[var(--text-primary)] py-2 text-center text-xs font-semibold text-white/90 transition-colors hover:text-white disabled:opacity-60"
        >
          [DEV] Verify with {devCode}
        </button>
      )}

      <StepHeading title="Check your email">
        Enter the 6-digit code that just arrived. It confirms your identity for the
        consent you'll sign at the end.
      </StepHeading>

      {/* THE SAME COMPONENT the signing surface renders when a timed-out session
          sends a client back for a fresh code. The chrome differs (StepShell here,
          SigningShell there) and nothing else does. */}
      <VerifyCodeFields
        code={code}
        onCodeChange={(value) => {
          setCode(value);
          if (codeError) setCodeError("");
        }}
        error={codeError}
        email={deliveryEmail}
        onResend={() => void resend()}
        resending={resending}
        resent={resent}
      />
    </StepShell>
  );
}
